import type { Credential } from "@/lib/api/admin";
import type { AccessStatus } from "@/lib/api/accessStatus";
import { formatDate, formatRelative } from "@/lib/date";

import { accessSummary } from "./accessState";

/**
 * The Tenant connection page: its route, its four block anchors, and the one
 * line each block shows while it is closed.
 *
 * Every line is built from data that was read. A query still loading says so,
 * a query that failed says "unavailable", and nothing is ever filled in with
 * a plausible-looking default — a closed block that read "All granted" because
 * the status call failed would be the exact lie this console exists to avoid.
 */

export const TENANT_CONNECTION_PATH = "/admin/settings/tenant-connection";

export const TENANT_CONNECTION_ANCHORS = {
  entraApplication: "entra-application",
  tenantInit: "tenant-init",
  permissions: "permissions",
  capabilities: "capabilities",
} as const;

export type TenantConnectionAnchor =
  (typeof TENANT_CONNECTION_ANCHORS)[keyof typeof TENANT_CONNECTION_ANCHORS];

/** A tenant-relative path to one block of the page. */
export function tenantConnectionPath(anchor: TenantConnectionAnchor): string {
  return `${TENANT_CONNECTION_PATH}#${anchor}`;
}

/**
 * The retired settings pages and the block each one became. The server's
 * admin-consent return still lands on settings/consent?admin_consent=…, so
 * the redirect keeps the query string as well as adding the anchor.
 */
export const LEGACY_SETTINGS_PAGES: Record<string, TenantConnectionAnchor> = {
  connection: TENANT_CONNECTION_ANCHORS.entraApplication,
  "tenant-init": TENANT_CONNECTION_ANCHORS.tenantInit,
  permissions: TENANT_CONNECTION_ANCHORS.permissions,
  consent: TENANT_CONNECTION_ANCHORS.permissions,
  capabilities: TENANT_CONNECTION_ANCHORS.capabilities,
};

/** The /admin/tenant/:tab slugs from before the settings split. */
export const LEGACY_TENANT_TABS: Record<string, TenantConnectionAnchor> = {
  credentials: TENANT_CONNECTION_ANCHORS.entraApplication,
  capabilities: TENANT_CONNECTION_ANCHORS.capabilities,
  permissions: TENANT_CONNECTION_ANCHORS.permissions,
  consent: TENANT_CONNECTION_ANCHORS.permissions,
};

interface Read<T> {
  data: T | undefined;
  isPending: boolean;
  isError: boolean;
}

const LOADING = "Loading…";
const UNAVAILABLE = "Status unavailable";

export function entraApplicationSummary(creds: Read<Credential[]>): string {
  if (creds.isError) return UNAVAILABLE;
  if (creds.data === undefined) return LOADING;
  if (creds.data.length === 0) return "No credential registered";
  const first = creds.data[0];
  const name =
    creds.data.length === 1 ? first.display_name : `${creds.data.length} credentials`;
  if (first.last_verification_ok === null || first.last_verified_at === null) {
    return `${name} · never verified`;
  }
  return first.last_verification_ok
    ? `${name} · verified ${formatDate(first.last_verified_at)}`
    : `${name} · last verification failed`;
}

export function tenantInitSummary(access: Read<AccessStatus>): string {
  if (access.isError) return UNAVAILABLE;
  if (access.data === undefined) return LOADING;
  const run = access.data.last_completed_run;
  const blocked = access.data.update_blocked_reason;
  // An open run is not "Not run", and it blocks any update.
  if (blocked === "run_open") {
    return run === null ? "Run in progress" : `Completed ${formatDate(run.completed_at)} · run in progress`;
  }
  const head = run === null ? "Not run" : `Completed ${formatDate(run.completed_at)}`;
  if (!access.data.update_available) return head;
  const missing = access.data.pending.missing;
  const grant = missing > 0 ? ` (${missing} to grant)` : "";
  // The server says an update is due but cannot start: say so, never
  // "available" over a block the update action inside will refuse.
  return blocked !== null ? `${head} · update blocked${grant}` : `${head} · update available${grant}`;
}

export function permissionsSummary(access: Read<AccessStatus>): string {
  if (access.isError) return UNAVAILABLE;
  if (access.data === undefined) return LOADING;
  const line = accessSummary(access.data.permissions);
  const manual = access.data.pending.manual;
  const parts = [line === "" ? "No permission rows" : line];
  // The role rows the block shows and an update grants — the same rows the
  // server counts as pending (non-manual, needed by a switched-on module).
  const roles = access.data.roles.filter((r) => !r.manual && r.modules.length > 0);
  const rolesMissing = roles.filter((r) => r.state === "missing").length;
  const rolesUnknown = roles.filter((r) => r.state === "unknown").length;
  if (rolesMissing > 0) parts.push(`${rolesMissing} role${rolesMissing === 1 ? "" : "s"} missing`);
  if (rolesUnknown > 0) parts.push(`${rolesUnknown} role${rolesUnknown === 1 ? "" : "s"} unknown`);
  if (manual > 0) parts.push(`${manual} manual`);
  return parts.join(" · ");
}

export function capabilitiesSummary(access: Read<AccessStatus>, now: Date = new Date()): string {
  if (access.isError) return UNAVAILABLE;
  if (access.data === undefined) return LOADING;
  if (access.data.credential_id === null) return "No credential to probe";
  if (access.data.probed_at === null) return "Not probed";
  return `Probed ${formatRelative(access.data.probed_at, now)}`;
}
